import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { ParsedSentence } from '../../../../types';

// Fullscreen image overlay component
function FullscreenImageOverlay({
  imageUrl,
  onClose,
}: {
  imageUrl: string;
  onClose: () => void;
}) {
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <img
        src={imageUrl}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          objectFit: 'contain',
          borderRadius: '8px',
          cursor: 'default',
        }}
      />
    </div>,
    document.body
  );
}

interface ImageRendererProps {
  sentence: ParsedSentence;
  sentenceIdx: number;
  currentSentenceIdx: number;
  isNowPlaying: boolean;
  hasActiveSelection: boolean;
  sentenceRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
}

export function ImageRenderer({
  sentence,
  sentenceIdx,
  currentSentenceIdx,
  isNowPlaying,
  hasActiveSelection,
  sentenceRefs,
}: ImageRendererProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isCurrent = sentenceIdx === currentSentenceIdx;

  const getBackgroundColor = () => {
    if (isCurrent && isNowPlaying) return 'rgba(30, 74, 71, 0.15)';
    return 'transparent';
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Don't open fullscreen if in the middle of a selection
    if (hasActiveSelection) return;
    setIsFullscreen(true);
  };

  return (
    <>
      <div
        ref={(el) => {
          if (el)
            sentenceRefs.current.set(
              sentenceIdx,
              el as unknown as HTMLSpanElement
            );
        }}
        style={{
          margin: '1.5em 0',
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: getBackgroundColor(),
          transition: 'background-color 0.2s ease',
        }}
      >
        <img
          src={sentence.imageUrl}
          alt=""
          onClick={handleImageClick}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
            display: 'block',
            cursor: hasActiveSelection ? 'default' : 'pointer',
          }}
          loading="lazy"
        />
        {sentence.text && (
          <p
            style={{
              margin: '0.75em 0 0 0',
              fontSize: '0.9em',
              color: 'var(--text)',
              lineHeight: 1.6,
            }}
          >
            {sentence.text}
          </p>
        )}
      </div>
      {isFullscreen && sentence.imageUrl && (
        <FullscreenImageOverlay
          imageUrl={sentence.imageUrl}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </>
  );
}

// New component for image blocks with transcribed text
interface ImageBlockRendererProps {
  sentences: ParsedSentence[];
  startIdx: number;
  currentSentenceIdx: number;
  currentWordInfo: { sentenceIdx: number; wordIdx: number } | null;
  isNowPlaying: boolean;
  baseFontSize: number;
  hasActiveSelection: boolean;
  isSentenceSelected: (idx: number) => boolean;
  isAnchorSentence: (idx: number) => boolean;
  onSentenceClick: (idx: number, e: React.MouseEvent) => void;
  sentenceRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
  wordRefs: React.MutableRefObject<Map<string, HTMLSpanElement>>;
}

export function ImageBlockRenderer({
  sentences,
  startIdx,
  currentSentenceIdx,
  currentWordInfo,
  isNowPlaying,
  baseFontSize,
  hasActiveSelection,
  isSentenceSelected,
  isAnchorSentence,
  onSentenceClick,
  sentenceRefs,
  wordRefs,
}: ImageBlockRendererProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imageUrl = sentences[0]?.imageUrl;
  const narrationFontSize = baseFontSize * 0.8;

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasActiveSelection) return;
    setIsFullscreen(true);
  };

  // Render narration text with word-level highlighting
  const renderNarrationText = () => {
    return sentences.map((sentence, sentenceOffset) => {
      const idx = startIdx + sentenceOffset;
      const isCurrent = idx === currentSentenceIdx;
      const isSelected = isSentenceSelected(idx);
      const isAnchor = isAnchorSentence(idx);

      // Split into words while preserving whitespace
      const tokens = sentence.text.split(/(\s+)/);
      let localWordIdx = 0;

      const renderedTokens = tokens.map((token, tokenIdx) => {
        if (/^\s+$/.test(token)) {
          return <span key={`${idx}-ws-${tokenIdx}`}>{token}</span>;
        }

        const thisWordIdx = localWordIdx++;
        const isSpokenWord =
          isCurrent &&
          isNowPlaying &&
          currentWordInfo &&
          currentWordInfo.sentenceIdx === idx &&
          thisWordIdx <= currentWordInfo.wordIdx;

        const wordKey = `${idx}-${thisWordIdx}`;

        return (
          <span
            key={`${idx}-word-${tokenIdx}`}
            ref={(el) => {
              if (el) wordRefs.current.set(wordKey, el);
            }}
            style={{
              color: isSpokenWord ? 'var(--text)' : 'var(--text-secondary)',
              transition: 'color 0.08s',
            }}
          >
            {token}
          </span>
        );
      });

      return (
        <span
          key={idx}
          ref={(el) => {
            if (el)
              sentenceRefs.current.set(idx, el as unknown as HTMLSpanElement);
          }}
          onClick={(e) => onSentenceClick(idx, e)}
          className="sentence-selectable"
          style={{
            backgroundColor:
              isAnchor || isSelected
                ? 'rgba(30, 74, 71, 0.35)'
                : isCurrent && isNowPlaying
                  ? 'rgba(30, 74, 71, 0.2)'
                  : 'transparent',
            borderRadius: isAnchor || isSelected ? '2px' : '0',
            padding: isAnchor || isSelected ? '1px 0' : '0',
            cursor: 'pointer',
          }}
        >
          {renderedTokens}
          {sentenceOffset < sentences.length - 1 ? ' ' : ''}
        </span>
      );
    });
  };

  return (
    <>
      <div style={{ margin: '1.5em 0' }}>
        {/* Image */}
        <img
          src={imageUrl}
          alt=""
          onClick={handleImageClick}
          style={{
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '6px',
            display: 'block',
            cursor: hasActiveSelection ? 'default' : 'pointer',
          }}
          loading="lazy"
        />

        {/* Narration text - single paragraph */}
        <div
          style={{
            paddingLeft: '12px',
            borderLeft: '1px solid rgba(255,255,255,0.15)',
            marginLeft: '8px',
            marginTop: '8px',
            fontSize: `${narrationFontSize}rem`,
            lineHeight: 1.55,
          }}
        >
          {renderNarrationText()}
        </div>
      </div>
      {isFullscreen && imageUrl && (
        <FullscreenImageOverlay
          imageUrl={imageUrl}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </>
  );
}

interface SentenceRendererProps {
  sentence: ParsedSentence;
  sentenceIdx: number;
  currentSentenceIdx: number;
  currentWordInfo: { sentenceIdx: number; wordIdx: number } | null;
  isNowPlaying: boolean;
  isSentenceSelected: boolean;
  isSentenceAnchor: boolean;
  onSentenceClick: (idx: number, e: React.MouseEvent) => void;
  sentenceRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
  wordRefs: React.MutableRefObject<Map<string, HTMLSpanElement>>;
}

export default function SentenceRenderer({
  sentence,
  sentenceIdx,
  currentSentenceIdx,
  currentWordInfo,
  isNowPlaying,
  isSentenceSelected,
  isSentenceAnchor,
  onSentenceClick,
  sentenceRefs,
  wordRefs,
}: SentenceRendererProps) {
  const isCurrent = sentenceIdx === currentSentenceIdx;

  const getBackgroundColor = () => {
    if (isSentenceAnchor || isSentenceSelected) return 'rgba(30, 74, 71, 0.35)';
    if (isCurrent && isNowPlaying) return 'rgba(30, 74, 71, 0.15)';
    return 'transparent';
  };

  if (!sentence.words?.length || !isNowPlaying) {
    return (
      <span
        key={sentence.idx}
        ref={(el) => {
          if (el) sentenceRefs.current.set(sentenceIdx, el);
        }}
        className="sentence-selectable"
        onClick={(e) => onSentenceClick(sentenceIdx, e)}
        style={{
          color: 'var(--text)',
          backgroundColor: getBackgroundColor(),
          padding:
            isSentenceAnchor ||
            isSentenceSelected ||
            (isCurrent && isNowPlaying)
              ? '2px 0'
              : '0',
          borderRadius: isSentenceAnchor || isSentenceSelected ? '2px' : '0',
          cursor: 'pointer',
          transition: 'color 0.2s ease, background-color 0.2s ease',
        }}
      >
        {sentence.text}{' '}
      </span>
    );
  }

  // Render with word-level highlighting during playback
  const words = sentence.text.split(/(\s+)/);
  let wordIdx = 0;

  return (
    <span
      key={sentence.idx}
      ref={(el) => {
        if (el) sentenceRefs.current.set(sentenceIdx, el);
      }}
      className="sentence-selectable"
      onClick={(e) => onSentenceClick(sentenceIdx, e)}
      style={{
        backgroundColor:
          isSentenceAnchor || isSentenceSelected
            ? getBackgroundColor()
            : 'transparent',
        padding: isSentenceAnchor || isSentenceSelected ? '2px 0' : '0',
        borderRadius: isSentenceAnchor || isSentenceSelected ? '2px' : '0',
        cursor: 'pointer',
      }}
    >
      {words.map((token, tokenIdx) => {
        if (/^\s+$/.test(token)) {
          return <span key={tokenIdx}>{token}</span>;
        }

        const thisWordIdx = wordIdx++;
        const isSpokenWord =
          isCurrent &&
          currentWordInfo &&
          thisWordIdx <= currentWordInfo.wordIdx;
        const wordKey = `${sentenceIdx}-${thisWordIdx}`;

        return (
          <span
            key={tokenIdx}
            ref={(el) => {
              if (el) wordRefs.current.set(wordKey, el);
            }}
            style={{
              color: isSpokenWord ? 'var(--text)' : 'var(--text-secondary)',
              transition: 'color 0.08s',
            }}
          >
            {token}
          </span>
        );
      })}{' '}
    </span>
  );
}

interface HeadingRendererProps {
  sentence: ParsedSentence;
  sentenceIdx: number;
  currentSentenceIdx: number;
  isNowPlaying: boolean;
  isSentenceSelected: boolean;
  isSentenceAnchor: boolean;
  headingFontSize: number;
  onSentenceClick: (idx: number, e: React.MouseEvent) => void;
  sentenceRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
}

export function HeadingRenderer({
  sentence,
  sentenceIdx,
  currentSentenceIdx,
  isNowPlaying,
  isSentenceSelected,
  isSentenceAnchor,
  headingFontSize,
  onSentenceClick,
  sentenceRefs,
}: HeadingRendererProps) {
  const isCurrent = sentenceIdx === currentSentenceIdx;

  const getHeadingBackground = () => {
    if (isSentenceAnchor || isSentenceSelected) return 'rgba(30, 74, 71, 0.35)';
    if (isCurrent && isNowPlaying) return 'rgba(30, 74, 71, 0.15)';
    return 'transparent';
  };

  return (
    <h2
      key={sentence.idx}
      ref={(el) => {
        if (el)
          sentenceRefs.current.set(
            sentenceIdx,
            el as unknown as HTMLSpanElement
          );
      }}
      onClick={(e) => onSentenceClick(sentenceIdx, e)}
      className="sentence-selectable"
      style={{
        fontSize: `${headingFontSize}rem`,
        fontWeight: 600,
        lineHeight: 1.4,
        margin: sentenceIdx === 0 ? '0 0 0.75em 0' : '2em 0 0.75em 0',
        color: 'var(--text)',
        backgroundColor: getHeadingBackground(),
        padding:
          isSentenceAnchor || isSentenceSelected || (isCurrent && isNowPlaying)
            ? '4px 8px'
            : '0',
        marginLeft:
          isSentenceAnchor || isSentenceSelected || (isCurrent && isNowPlaying)
            ? '-8px'
            : '0',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'color 0.2s ease, background-color 0.2s ease',
      }}
    >
      {sentence.text}
    </h2>
  );
}
